import { DiscountsType } from 'components/Modals/EditDiscountModal/EditDiscountModal';
import { IPostCartOrderDTO } from 'pages/SupplierNewOrderPage/hooks';
import { cleanPricingQueryData } from 'providers/QueryProvider/helpers';
import apiClient from 'services/api/apiService';
import { AppMode } from 'store/Common/reducers/common';

const BASE_URL = '/v1/carts';

export const getShoppingCart = async (
  appMode: AppMode,
  cartId?: string | undefined
): Promise<any> => {
  const url = `${BASE_URL}/${cartId}`;
  const { data } = await apiClient.get<any>(url, {
    params: {
      ...(appMode === AppMode.BUYER && { company_mode: AppMode.BUYER }),
    },
  });
  return data.data;
};

// Cart id is also field that is optional, we use default cart for forms
export const addToShoppingCart = async (
  cartLineQuantity: number,
  model_id: number,
  company_id: number,
  lang: string,
  queryPayload: any,
  pricing: any,
  shoppingCartId?: string | undefined
): Promise<any> => {
  const url = `${BASE_URL}`;
  const cleanedPricing = cleanPricingQueryData(pricing);
  const { data } = await apiClient.post<any>(url, {
    quantity: cartLineQuantity,
    model_id,
    company_id,
    questions: queryPayload.questions,
    language_code: lang,
    pricing: cleanedPricing,
    ...(shoppingCartId && { cart_id: shoppingCartId }),
  });

  return data.data;
};

// Cart id is also field that is optional, we use default cart for forms
export const editShoppingCartItem = async (
  cartLineId: number,
  cartLineQuantity: number,
  modelId: number,
  companyId: number,
  lang: string,
  queryPayload: any,
  pricing: any,
  shoppingCartId?: string | undefined
): Promise<any> => {
  const url = `${BASE_URL}/${shoppingCartId}/${cartLineId}`;
  const cleanedPricing = cleanPricingQueryData(pricing);
  const { data } = await apiClient.put<any>(url, {
    quantity: cartLineQuantity,
    model_id: modelId,
    company_id: companyId,
    language_code: lang,
    questions: queryPayload.questions,
    pricing: cleanedPricing,
  });
  return data.data;
};

export const postCartOrder = async (
  postCardOrderDTO: IPostCartOrderDTO
): Promise<any> => {
  const url = `${BASE_URL}/order`;
  const { data } = await apiClient.post<any>(url, postCardOrderDTO);
  return data.data;
};

export const deleteShoppingCartItem = async (
  cartLineId: number,
  shoppingCartId?: string | undefined
): Promise<any> => {
  const url = `${BASE_URL}/${shoppingCartId}/${cartLineId}`;

  const { data } = await apiClient.delete<any>(url, {});

  return data.data;
};

export const duplicateCartLine = async (
  cartLineId: number,
  shoppingCartId?: string | undefined
): Promise<any> => {
  const url = `${BASE_URL}/${shoppingCartId}/${cartLineId}/duplicate`;

  const { data } = await apiClient.post<any>(url);

  return data.data;
};

export const changeQuantityCartLine = async (
  cartLineId: number,
  quantity: number
): Promise<any> => {
  const payload = {
    quantity,
  };
  const url = `/v1/cart-lines/${cartLineId}/quantity`;
  const { data } = await apiClient.patch<any>(url, payload);

  return data.data;
};

// Order carts are tied to a supplier (company_*_id); quotation carts are
// user-level, so they send only is_quotation_cart and omit company_*_id.
interface ICartLookup {
  companyId?: number;
  appMode?: AppMode;
  isQuotationCart?: boolean;
}

const getCartLookupParams = ({
  companyId,
  appMode,
  isQuotationCart,
}: ICartLookup) =>
  isQuotationCart
    ? { is_quotation_cart: true }
    : {
        ...(appMode === AppMode.SUPPLIER && { company_buyer_id: companyId }),
        ...(appMode === AppMode.BUYER && { company_seller_id: companyId }),
      };

// New
export const createNewCart = async (params: ICartLookup): Promise<any> => {
  const url = `${BASE_URL}/new`;
  const { data } = await apiClient.post<any>(url, getCartLookupParams(params));
  return data.data;
};

export const checkIfCartExists = async (params: ICartLookup): Promise<any> => {
  const url = `${BASE_URL}/check`;
  const { data } = await apiClient.get<any>(url, {
    params: getCartLookupParams(params),
  });

  return data.data;
};

export const changeDiscountCartLine = async (
  cartId: string,
  cartLineId: number,
  discountAmount: string | null,
  discountType: DiscountsType
): Promise<any> => {
  const payload = {
    discount_amount:
      discountAmount === '' || discountAmount === null ? null : discountAmount,
    discount_type: discountType,
  };
  const url = `${BASE_URL}/${cartId}/${cartLineId}/discount`;
  const { data } = await apiClient.patch<any>(url, payload);

  return data.data;
};
